/**
 * Small pure helpers shared by the composer panes.
 *
 * The important one is the hashtag pair: connectors publish `content` verbatim,
 * so a hashtag only ships if it is *in the caption*. Every mutation below keeps
 * `meta.hashtags` and the caption in step.
 */

import { extractHashtags, validatePlatformContent } from '../../../shared/platforms';
import type {
  MediaAsset,
  PlatformId,
  PlatformPost,
  PlatformPostMeta,
  ValidationIssue,
} from '../../../shared/types';
import { escapeRegExp, normalizeHashtags } from '../../../shared/utils';
import { api } from '../../lib/api';
import type { DraftValue } from './types';

/** `<img src>` for a stored asset — carries the workspace id the header cannot. */
export function assetUrl(asset: Pick<MediaAsset, 'r2Key'>): string {
  return api.media.fileUrl(asset.r2Key);
}

/** Hashtags shown in the chip row: explicit meta wins, else whatever is typed. */
export function draftHashtags(value: DraftValue): string[] {
  const meta = value.meta.hashtags;
  return normalizeHashtags(meta && meta.length ? meta : extractHashtags(value.content));
}

export function contentHasHashtag(content: string, tag: string): boolean {
  return new RegExp(`(^|\\s)${escapeRegExp(tag)}(?![\\p{L}\\p{N}_])`, 'iu').test(content);
}

/**
 * Everything that actually reaches the platform. Instagram's first comment goes
 * out with the post, so a tag parked there publishes just as surely as one in
 * the caption.
 */
export function publishedText(value: DraftValue): string {
  return [value.content, value.meta.firstComment ?? ''].join('\n');
}

function tidy(content: string): string {
  return content
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .replace(/[ \t]+$/, '');
}

/** Append tags to the caption (skipping ones already going out) and record them. */
export function addHashtags(value: DraftValue, tags: readonly string[]): DraftValue {
  const wanted = normalizeHashtags(tags);
  if (!wanted.length) return value;

  const shipped = publishedText(value);
  const missing = wanted.filter((tag) => !contentHasHashtag(shipped, tag));
  const content = missing.length
    ? tidy(`${value.content.trimEnd()}${value.content.trim() ? '\n\n' : ''}${missing.join(' ')}`)
    : value.content;

  const meta: PlatformPostMeta = {
    ...value.meta,
    hashtags: normalizeHashtags([...draftHashtags(value), ...wanted]),
  };
  return { ...value, content, meta };
}

/** Remove a tag from the caption, the first comment and the recorded list. */
export function removeHashtag(value: DraftValue, tag: string): DraftValue {
  const pattern = new RegExp(`(^|\\s)${escapeRegExp(tag)}(?![\\p{L}\\p{N}_])`, 'giu');
  const content = tidy(value.content.replace(pattern, '$1'));
  const meta: PlatformPostMeta = {
    ...value.meta,
    hashtags: draftHashtags(value).filter((item) => item.toLowerCase() !== tag.toLowerCase()),
  };
  if (meta.firstComment) meta.firstComment = tidy(meta.firstComment.replace(pattern, '$1'));
  return { ...value, content, meta };
}

/** Recorded hashtags that would not actually go out with the post. */
export function danglingHashtags(value: DraftValue): string[] {
  const shipped = publishedText(value);
  return draftHashtags(value).filter((tag) => !contentHasHashtag(shipped, tag));
}

/**
 * Assets a variant will actually publish. An empty selection means "whatever is
 * attached to the post" — the same fallback the publisher applies server-side.
 */
export function variantAssets(value: DraftValue, postAssets: MediaAsset[]): MediaAsset[] {
  if (!value.media.length) return postAssets;
  const byId = new Map(postAssets.map((asset) => [asset.id, asset]));
  return value.media
    .map((item) => byId.get(item.assetId))
    .filter((asset): asset is MediaAsset => Boolean(asset));
}

/** Live validation for one variant — the same rules the connectors enforce. */
export function issuesFor(
  variant: PlatformPost,
  value: DraftValue,
  postAssets: MediaAsset[],
): ValidationIssue[] {
  const assets = variantAssets(value, postAssets);
  return validatePlatformContent(variant.platform, {
    content: value.content,
    mediaCount: assets.length,
    videoCount: assets.filter((asset) => asset.kind === 'video').length,
    hasLink: /https?:\/\//.test(value.content) || Boolean(value.meta.linkUrl),
    title: value.meta.title ?? null,
    hashtags: draftHashtags(value),
    hasAccount: Boolean(variant.socialAccountId),
  });
}

export function hasBlockingIssue(issues: ValidationIssue[]): boolean {
  return issues.some((issue) => issue.level === 'error');
}

/** First line of the raw notes, used as a fallback post title. */
export function deriveTitle(rawContent: string): string {
  const line = rawContent.split('\n').find((entry) => entry.trim().length > 0) ?? '';
  return line.trim().replace(/^[-*•#\s]+/, '').slice(0, 120);
}

/** Reading order for the platform tabs — blogs last, coming-soon after that. */
export function platformOrder(platform: PlatformId): number {
  const order: PlatformId[] = ['x', 'instagram', 'facebook', 'linkedin', 'wordpress', 'tiktok', 'youtube'];
  const index = order.indexOf(platform);
  return index === -1 ? order.length : index;
}
